/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import moment from 'moment-timezone';

// Component imports
import { FilterFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Col, Row, Table, Typography } from 'antd';
import { ArrowDownIcon } from '../../Icons/ArrowDownIcon';
import { ArrowRightIcon } from '../../Icons/ArrowRightIcon';

// Misc imports
import { DATETIME_DATETIME_TIMEZONE_MILLISECONDS_FORMAT, INTERACTION_TYPE_FILTERS } from '../../../constants/main';
import { useFetchEventIdInteractionsMetadataQuery } from '../../../store/interactionMetadata/interactionMetadataApi';
import { isEmptyObject } from '../../../utils/is/isEmptyObject';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import JsonViewer from '../JsonViewer';

const SessionInteractionLogTab = ({
  selectedChatSessionMessage
}) => {

  const { orgId, tenantId } = useParams();

  const [tableParams, setTableParams] = useState({
    filters: {},
    sorter: {}
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const { data: bot, isLoading: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: interactionLogs, isLoading: isLoadingInteractionLogs } = useFetchEventIdInteractionsMetadataQuery(
    {
      orgId,
      eventId: selectedChatSessionMessage?.id
    }, {
      skip: isEmptyObject(selectedChatSessionMessage)
    });

  useEffect(() => {
    return () => {
      setTableParams({
        filters: {},
        sorter: {}
      });
    };
  }, []);

  const handleExpand = useCallback((expanded, record) => {
    if (expanded) {
      setExpandedRowKeys(prevKeys => [...prevKeys, record.id]);
    } else {
      setExpandedRowKeys(prevKeys => prevKeys.filter(key => key !== record.id));
    }
  }, []);

  const getRowClassName = useCallback((record) => {
    return expandedRowKeys.includes(record.id) ? 'row-is-expanded' : '';
  }, [expandedRowKeys]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setTableParams({
      filters,
      sorter
    });
  }, []);

  const columns = useMemo(() => [
    {
      title: t('Timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      sorter: (a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf(),
      defaultSortOrder: 'descend',
      sortDirections: ['descend', 'ascend'],
      sortOrder: tableParams.sorter.field === 'timestamp' && tableParams.sorter.order,
      width: '50%',
      render: (timestamp) => (
        <Typography.Text>
          {moment(timestamp).tz(bot?.timezone || 'UTC').format(DATETIME_DATETIME_TIMEZONE_MILLISECONDS_FORMAT)}
        </Typography.Text>
      )
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      filters: INTERACTION_TYPE_FILTERS,
      filterMultiple: true,
      onFilter: (value, record) => record.type === value,
      filteredValue: tableParams.filters.type || null,
      width: '25%',
      render: (value) => {
        const label = INTERACTION_TYPE_FILTERS.find(type => type.value === value)?.text || value;
        return <Typography.Text>{label}</Typography.Text>;
      }
    },
    {
      title: (
        <Row>
          <Col xs={24}>
            {t('Errors')}
            <FilterFilled
              className="ml-2 cursor-pointer"
              style={{
                color: tableParams.filters.isError ? '#1890ff' : undefined
              }}
              onClick={(e) => {
                e.stopPropagation();
                const currentErrorFilter = tableParams.filters.isError;
                const newFilters = {
                  ...tableParams.filters,
                  isError: currentErrorFilter ? null : true
                };
                handleTableChange(undefined, newFilters, tableParams.sorter);
              }}
            />
          </Col>
        </Row>
      ),
      dataIndex: 'isError',
      key: 'isError',
      render: (isError) => {
        return isError ? <InfoCircleOutlined className="text-error" /> : null;
      },
      width: '25%'
    }
  ], [tableParams, t, handleTableChange, bot]);

  const tableData = useMemo(() => {
    if(tableParams?.filters?.isError) {
      return interactionLogs?.items?.filter(item => item?.isError);
    }
    return interactionLogs?.items;
  }, [tableParams?.filters?.isError, interactionLogs?.items]);

  return (
    <Row className="px-2">
      <Col xs={24}>
        <Table
          rowKey="id"
          loading={isLoadingInteractionLogs || isFetchingBot}
          className="interactions-logs-table table-scroll"
          rowClassName={getRowClassName}
          scroll={{ y: 500 }}
          dataSource={tableData}
          columns={columns}
          onChange={handleTableChange}
          pagination={false}
          expandable={{
            expandedRowKeys: expandedRowKeys,
            onExpand: handleExpand,
            expandRowByClick: true,
            expandedRowRender: (record) => (
              <Row className="py-4 interaction-logs-expanded-row" style={{ paddingLeft: '60px' }}>
                <Col xs={24}>
                  <JsonViewer jsonObject={record.payload} />
                </Col>
              </Row>
            ),
            rowExpandable: (record) => record.payload && !isEmptyObject(record.payload),
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <ArrowDownIcon
                  style={{ cursor: 'pointer' }}
                  onClick={e => onExpand(record, e)}
                />
              ) : (
                <ArrowRightIcon
                  style={{ cursor: 'pointer' }}
                  onClick={e => onExpand(record, e)}
                />
              )
          }}
        />
      </Col>
    </Row>

  );
};

SessionInteractionLogTab.propTypes = {
  selectedChatSessionMessage: PropTypes.object
};

export default SessionInteractionLogTab;
